<template>
  <div>
    <span v-if="object[field.id]">{{ $t('yes') }}</span>
    <span v-if="object.fiscal_url" class="ml-2">
      <a :href="object.fiscal_url" target="_blank">
        <LinkSvg class="svg-size-md svg-light"/>
      </a>
    </span>
    <div v-if="!object[field.id]">
      <span class="text-secondary">{{ $t('no') }}</span>
      <a @click="createCheque"
         v-if="rights.includes('cashdesk') && object.forma != 7 && !object.deleted_at && object.treatment_id"
         class="ml-2 d-inline-block pointer opacity-hover"
         :title="$t('create_cheque')"
         v-b-tooltip.hover>
        <ChequeSvg class="svg-light svg-size-md"/>
      </a>
      <a v-if="canBeMarkedManually"
         :title="$t('market_as_fiscal_cheque_manually')"
         v-b-tooltip.hover
         class="ml-2 pointer"
         @click="markAsCheque">
        <TickSvg class="svg-lighter svg-size-md"/>
      </a>
    </div>
  </div>
</template>

<script setup>
import ChequeSvg from '@/assets/svg-vue/patients/cheque.svg'
import TickSvg from "@/assets/svg-vue/form/tick.svg.vue"
import LinkSvg from '@/assets/svg-vue/general/link.svg'
import {computed, getCurrentInstance, onBeforeMount, ref} from "vue"
import { useStore } from "vuex"
import { EloquentService } from "@/services/api/eloquent.api.service"
import KKMService from "@/services/kkm.service"
import { copyObject } from "@/extensions/prototypes/prototypes"
import { PatientService } from "@/services/api/patient.api.service"

const store = useStore()

const props = defineProps({
  object: {
    type: Object
  },
  field: {}
})

let i18n = null
let noty = null

const generatingCheque = ref(false)

const loading = computed(() => store.state.dom.loading)
const rights = computed(() => store.state.auth.rights)
const kkmEnabled = computed(() => store.getters.kkmEnabled)
const canBeMarkedManually = computed(() => {
  return (props.object.fiscal_vendor === 'kkm' || !props.object.fiscal_vendor)
      && rights.value.includes('cashdesk')
      && rights.value.includes('accept_payments')
      && kkmEnabled.value
})

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
})

const createCheque = async () => {
  if(loading.value || generatingCheque.value) return
  try {
    generatingCheque.value = true
    const payment = props.object
    if(kkmEnabled.value) {
      const paymentRes = await EloquentService.show('payment', payment.id)
      if(paymentRes?.data?.fiscal_passed) return
      const isRefund = Number(paymentRes?.data?.paid || 0) < 0
      const services = isRefund ? [] : (paymentRes?.data?.record?.servicesWithDetailsAttribute?.services || [])
      const paid = Math.abs(Number(payment.paid))
      const totalPrice = isRefund ? paid : Math.abs(Number(paymentRes?.data?.record?.price || 0))
      const totalPaid = isRefund ? paid : Math.abs(Number(paymentRes?.data?.record?.paidSum || 0))
      const kkmData = {
        totalPrice: totalPrice,
        totalPaid: totalPaid,
        payId: payment.id,
        amount: paid,
        payMethod: payment.forma,
        services: copyObject(services),
        numDevice: 0,
        transactionType: isRefund ? 'refund' : 'payForVisit',
        email: null,
      }

      const chequeRes = await KKMService.printCheque(kkmData)
      if(chequeRes?.error) {
        noty.error(i18n.t('error_printing_cheque') + ': ' + chequeRes.error)
      }
      if(chequeRes) {
        await PatientService.setUUIDForPayment(payment.id, {
          uuid: chequeRes.data?.CheckNumber || null,
          allData: chequeRes.data,
          fiscal_vendor: 'kkm',
        })
        store.commit('incrementDataTableCounter')
      }
    }
    if(!kkmEnabled.value) {
      const chequeRes = await PatientService.generateChequeForPayment(payment.id, {})
      if(chequeRes?.data?.fiscal_vendor) {
        noty.info(i18n.t('cheque_generation_in_process_status_might_change_later'))
        store.commit('incrementDataTableCounter')
      } else {
        noty.error(i18n.t('error_message'))
      }
    }
  } catch (e) {
    console.log(e)
  } finally {
    setTimeout(() => {
      generatingCheque.value = false
    }, 100)
  }

}

const markAsCheque = async () => {
  const chequeId = prompt(i18n.t('enter_the_cheque_number'))
  if(chequeId) {
    try {
      await PatientService.setUUIDForPayment(props.object.id, {
        uuid: chequeId,
        fiscal_vendor: 'kkm',
      })
    } catch (error) {
      console.log(error)
    }
    store.commit('incrementDataTableCounter')
  }
}
</script>

<style lang="scss">
.opacity-hover {
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
}
</style>
